/**
 * Teams App Theme Configuration
 * ============================================================================
 * TypeScript theme constants for Microsoft Teams Tab applications.
 * Uses Fluent UI v9 design tokens as the foundation with Teams-specific
 * density and color overrides.
 *
 * TEMPLATE: Replace placeholder values with project-specific branding as needed.
 * The Teams Purple (#6264A7) and density settings should remain constant.
 *
 * Usage:
 *   import { teamsTheme } from '../styles/teams-theme';
 *   const gap = teamsTheme.spacing.l; // '16px'
 *
 * Paired with:
 *   - teams-theme.css  (CSS custom properties + Fluent UI overrides)
 *   - TeamsAppLayout.tsx (LAYOUT-TEAMS-APP component)
 * ============================================================================
 */

export const teamsTheme = {
  // ---------------------------------------------------------------------------
  // Colors — Teams Modern Palette
  // ---------------------------------------------------------------------------
  colors: {
    /** Teams brand purple — primary actions, active states, links */
    primary: '#6264A7',
    primaryHover: '#464775',
    primaryPressed: '#33344A',

    secondary: '#F3F2F1',
    secondaryHover: '#EDEBE9',

    success: '#92C353',
    warning: '#F8D22A',
    error: '#C4314B',
    info: '#00BCF2',

    /** Page background behind content area */
    background: '#FAFAFA',
    backgroundHover: '#F5F5F5',
    /** Card / surface background */
    surface: '#FFFFFF',
    surfaceHover: '#F9F9F9',

    text: {
      primary: '#1F1F1F',
      secondary: '#6B6B6B',
      disabled: '#B0B0B0',
      inverse: '#FFFFFF',
    },

    border: {
      default: '#E8E8E8',
      hover: '#D0D0D0',
      active: '#6264A7',
    },

    /** Sidebar-specific colours */
    sidebar: {
      background: '#FFFFFF',
      hoverBackground: '#F5F5F5',
      activeBackground: '#EBF3FC',
      activeText: '#6264A7',
      text: '#424242',
      textSecondary: '#6B6B6B',
    },
  },

  // ---------------------------------------------------------------------------
  // Spacing — 8 px Grid System
  // ---------------------------------------------------------------------------
  spacing: {
    none: '0',
    xxs: '2px',
    xs: '4px',
    s: '8px',
    m: '12px',
    l: '16px',
    xl: '20px',
    xxl: '24px',
    xxxl: '32px',
    huge: '40px',
  },

  // ---------------------------------------------------------------------------
  // Typography — Teams Chat Density
  // ---------------------------------------------------------------------------
  typography: {
    /** Page-level hero headings */
    display: {
      fontSize: '28px',
      fontWeight: '600' as const,
      lineHeight: '36px',
    },
    /** Section headings (e.g. dashboard panels) */
    title: {
      fontSize: '20px',
      fontWeight: '600' as const,
      lineHeight: '28px',
    },
    /** Card titles, settings group labels */
    subtitle: {
      fontSize: '16px',
      fontWeight: '600' as const,
      lineHeight: '22px',
    },
    /** Default body text */
    body: {
      fontSize: '14px',
      fontWeight: '400' as const,
      lineHeight: '20px',
    },
    /** Secondary / helper text */
    caption: {
      fontSize: '12px',
      fontWeight: '400' as const,
      lineHeight: '16px',
    },
    /** Badge labels, compact metadata */
    small: {
      fontSize: '11px',
      fontWeight: '400' as const,
      lineHeight: '14px',
    },
  },

  // ---------------------------------------------------------------------------
  // Shadows — Lighter Elevation (Teams Density)
  // ---------------------------------------------------------------------------
  shadows: {
    none: 'none',
    /** Default card resting state */
    card: '0 0.3px 0.9px rgba(0, 0, 0, 0.06), 0 1.6px 3.6px rgba(0, 0, 0, 0.08)',
    /** Card hover — subtle lift */
    cardHover: '0 2px 8px rgba(0, 0, 0, 0.08), 0 0.6px 1.8px rgba(0, 0, 0, 0.06)',
    /** Sidebar, floating panels */
    elevated: '0 4px 12px rgba(0, 0, 0, 0.1), 0 1.2px 3.6px rgba(0, 0, 0, 0.08)',
    /** Modal dialogs */
    dialog: '0 20px 50px rgba(0, 0, 0, 0.15), 0 4.8px 14.4px rgba(0, 0, 0, 0.12)',
    /** Dropdown menus, flyouts */
    flyout: '0 4px 12px rgba(0, 0, 0, 0.1), 0 1.2px 3.6px rgba(0, 0, 0, 0.08)',
  },

  // ---------------------------------------------------------------------------
  // Border Radius
  // ---------------------------------------------------------------------------
  borderRadius: {
    none: '0',
    small: '4px',
    medium: '8px',
    large: '12px',
    circular: '50%',
  },

  // ---------------------------------------------------------------------------
  // Transitions — Fluent Motion
  // ---------------------------------------------------------------------------
  transitions: {
    fast: '100ms cubic-bezier(0.33, 0, 0.67, 1)',
    normal: '200ms cubic-bezier(0.33, 0, 0.67, 1)',
    slow: '300ms cubic-bezier(0.33, 0, 0.67, 1)',
  },

  // ---------------------------------------------------------------------------
  // Layout Constants — Teams Tab Constraints
  // ---------------------------------------------------------------------------
  layout: {
    /** Sidebar fixed width */
    sidebarWidth: '260px',
    /** Sidebar collapsed width (if implementing collapse) */
    sidebarCollapsedWidth: '56px',
    /** Brand header height inside sidebar top */
    sidebarHeaderHeight: '56px',
    /** Toolbar / controls bar height */
    toolbarHeight: '48px',
    /** Maximum content width to prevent ultra-wide reading */
    maxContentWidth: '1600px',
    /** Recommended max width for settings / form pages */
    settingsMaxWidth: '1000px',
  },

  // ---------------------------------------------------------------------------
  // Page Patterns — Common Configurations
  // ---------------------------------------------------------------------------
  pagePatterns: {
    /** Dashboard: stats grid + recent items + pending items */
    dashboard: {
      statsGridColumns: 4,
      statsGridGap: '16px',
      sectionGap: '24px',
    },
    /** Content split: sidebar tree + main panel (e.g. document list) */
    contentSplit: {
      sidebarWidth: '280px',
      gap: '0',
    },
    /** Controls bar: search + filter + actions row */
    controlsBar: {
      height: '48px',
      gap: '12px',
    },
    /** Settings: sectioned cards with input groups */
    settings: {
      maxWidth: '1000px',
      sectionGap: '24px',
      inputGroupGap: '16px',
    },
  },
} as const;

/**
 * Type export for strong typing in consuming components.
 *
 * Usage:
 *   import type { TeamsTheme } from '../styles/teams-theme';
 */
export type TeamsTheme = typeof teamsTheme;

/**
 * Role badge colour mapping for Teams App layouts.
 * Consistent with LAYOUT-SIDEBAR-MODERN role colours.
 */
export const TEAMS_ROLE_BADGE_COLORS = {
  admin: 'danger',       // Red — highest privilege
  approver: 'important', // Orange / important
  editor: 'brand',       // Brand colour
  user: 'subtle',        // Neutral / subtle
} as const;
